/**
 * The column beside the live room, as the confirmed prototype draws it: Room (the group chat), People, Hands (once
 * the live is on), and the host's own face for the private chat. On a laptop it tucks away; on a phone it is a sheet.
 *
 * The group chat is the room's own socket (`src/live/room/chat.ts`), with its pin, its refusals and its 500
 * characters; the private chat is the same thread as everywhere else (`useWatchThread`).
 */
import { type FormEvent, memo, type ReactNode, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { CHAT_MAX_CHARS, type ChatLine, type ChatToRoom, type PresenceFace } from "../../../shared/live-protocol";
import type { WatchSession } from "../../../shared/types";
import { type Meeting, type MeetingFace, type MeetingState, handsInOrder } from "../../live/meeting/meeting";
import { questionsIn } from "../../live/questions";
import { useFollowChat } from "../../live/useFollowChat";
import { firstWord, initialsOf, photoOfHost } from "../../live/meeting/table";
import { type RoomChat as RoomChatState, refusalWords } from "../../live/room/chat";
import { fmt } from "../../lib/when";
import { GIcon } from "../room/GIcon";
import { RoomChat } from "../room/RoomChat";
import type { WatchThread } from "../WatchPageView";
import { type HostKit, HostQuestions, ShareRules } from "./hostParts";
import { faceWords } from "./liveParts";

export type ColumnTab = "room" | "people" | "hands" | "maya" | "questions" | "moments";

/** The host's side of the column: the same tabs, with what only the host does on them. */
export interface HostColumn {
  kit: HostKit;
  meeting: Meeting;
  tools: MeetingState["tools"];
  /** Share asks plus hands: what the Hands tab counts. */
  waiting: number;
  /** Something new arrived while the host was on another tab. */
  lit: boolean;
}

/** The room shows a running count over 400 characters, and says so plainly past 500. */
const COUNT_FROM = 400;

/** "5:56": a line's time on this person's clock, short. */
function lineTime(at: number): string {
  return new Date(at * 1000).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(/\s?[AP]M$/, "");
}

const GroupLine = memo(function GroupLine({ line, hostAvatar, hostName, tools, pinned }: { line: ChatLine; hostAvatar: string | null; hostName: string; tools?: HostKit["lineTools"]; pinned?: boolean }): ReactNode {
  const photo = line.host ? photoOfHost(line.name, hostName, hostAvatar) : (line.photo ?? null);
  const face = photo ? <img className="av" src={photo} alt="" /> : <span className="av av-i">{initialsOf(line.name)}</span>;
  return (
    <div className={line.mine ? "gm mine" : "gm"}>
      {face}
      <div className="gm-b">
        <span className="gm-n">
          {line.mine ? "You" : <bdi>{line.name}</bdi>}
          {line.host ? <em>Host</em> : null}
          <i>{lineTime(line.at)}</i>
        </span>
        <p dir="auto">{line.body}</p>
        {tools ? (
          <span className="gm-acts">
            <button type="button" onClick={() => tools.pin(pinned ? null : line.id)}>
              {pinned ? "Unpin" : "Pin"}
            </button>
            {line.registrantId ? (
              <button type="button" onClick={() => tools.reply(line)}>
                Reply privately
              </button>
            ) : null}
            <button type="button" onClick={() => tools.hide(line.id)}>
              Hide
            </button>
          </span>
        ) : null}
      </div>
    </div>
  );
});

/** The room's group chat: the pin, who said what, and the field to say something. */
function GroupChat({
  chat,
  hostFirst,
  hostName,
  hostAvatar,
  opensAt,
  liveAt,
  ended,
  reconnecting,
  send,
  onToHost,
  tools,
}: {
  chat: RoomChatState;
  hostFirst: string;
  hostName: string;
  hostAvatar: string | null;
  /** "5:50 PM": when the room opened, on this person's clock. */
  opensAt: string;
  /** When the live started (unix seconds), for the line that marks it. */
  liveAt: number | null;
  ended: boolean;
  reconnecting: boolean;
  send: (msg: ChatToRoom) => boolean;
  onToHost: () => void;
  tools?: HostKit["lineTools"];
}): ReactNode {
  const log = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [note, setNote] = useState<{ words: string; bad: boolean } | null>(null);
  const lastSent = useRef("");
  const follow = useFollowChat(log, chat.lines);

  // A message the room turned down comes back to the field, so nothing typed is lost.
  useEffect(() => {
    if (!chat.refused) return;
    setNote({ words: refusalWords(chat.refused), bad: true });
    if (lastSent.current) setText((now) => now || lastSent.current);
  }, [chat.refused]);

  const typed = (value: string) => {
    setText(value);
    const el = field.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(120, el.scrollHeight)}px`;
    }
    const n = value.length;
    if (n > CHAT_MAX_CHARS) setNote({ words: `${fmt(n)} / ${CHAT_MAX_CHARS}. That’s over ${CHAT_MAX_CHARS} characters, shorten it a little.`, bad: true });
    else setNote(n >= COUNT_FROM ? { words: `${n} / ${CHAT_MAX_CHARS}`, bad: false } : null);
  };

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body) return;
    if (body.length > CHAT_MAX_CHARS) {
      setNote({ words: `That’s ${fmt(body.length)} characters. Keep it to ${CHAT_MAX_CHARS} so everyone can read it.`, bad: true });
      return;
    }
    if (reconnecting || !send({ type: "say", body })) {
      setNote({ words: "The room is reconnecting. Try again in a moment.", bad: true });
      return;
    }
    lastSent.current = body;
    setText("");
    setNote(null);
    if (field.current) field.current.style.height = "auto";
  };

  const markAt = liveAt === null ? -1 : chat.lines.findIndex((l) => l.at >= liveAt);
  const liveMark = (
    <p className="gm-sys live" key="live">
      <span className="live-dot" />
      {ended ? `${hostFirst} went live` : `${hostFirst} is live`}
    </p>
  );

  return (
    <>
      {chat.pinned && !ended ? (
        <div className="gpin">
          <GIcon name="pin" size={16} />
          <span>
            <b><bdi>{chat.pinned.host ? firstWord(chat.pinned.name) : chat.pinned.name}</bdi>:</b> <span dir="auto">{chat.pinned.body}</span>
          </span>
        </div>
      ) : null}
      <div className="glog" ref={log} role="log" aria-label="The room’s chat">
        {opensAt ? <p className="gm-sys">The room opened at {opensAt}</p> : null}
        {chat.lines.map((line, i) => (
          <div key={line.id} style={{ display: "contents" }}>
            {i === markAt ? liveMark : null}
            <GroupLine line={line} hostAvatar={hostAvatar} hostName={hostName} tools={tools} pinned={chat.pinned?.id === line.id} />
          </div>
        ))}
        {liveAt !== null && markAt === -1 ? liveMark : null}
        {ended ? <p className="gm-sys"><bdi>{hostFirst}</bdi> ended the live</p> : null}
      </div>
      {follow.unseen > 0 ? (
        <button className="gnew" type="button" onClick={follow.toNewest}>
          <GIcon name="chd" size={16} /> {fmt(follow.unseen)} new message{follow.unseen === 1 ? "" : "s"}
        </button>
      ) : null}
      <div className="gin">
        {ended ? (
          <p className="closed">
            <GIcon name="lock" size={16} /> The live chat is closed.{" "}
            <button type="button" onClick={onToHost}>
              Message {firstWord(hostName)}
            </button>
          </p>
        ) : (
          <form onSubmit={submit} noValidate>
            <div className="chat-row">
              <textarea
                ref={field}
                dir="auto"
                rows={1}
                placeholder="Say hello to the room"
                aria-label="Say something to the room"
                value={text}
                onChange={(e) => typed(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" || e.shiftKey) return;
                  e.preventDefault();
                  submit();
                }}
              />
              <button className="send" type="submit" aria-label="Send to the room">
                <GIcon name="send" size={18} stroke={2.2} />
              </button>
            </div>
            <p className={note?.bad ? "gin-n bad" : "gin-n"}>{note?.words ?? ""}</p>
          </form>
        )}
      </div>
    </>
  );
}

/** One person in the people list: their face, name, what they are doing, and their mic and camera. */
function PersonRow({ name, you, avatar, words, micOn, cameraOn, place, actions, fixed }: { fixed?: boolean; name: string; you?: boolean; avatar?: string | null; words: string; micOn: boolean; cameraOn: boolean; place?: number; actions?: ReactNode }): ReactNode {
  return (
    <div className={fixed ? "pp-r pp-fix" : "pp-r"}>
      {place ? <span className="pp-o">{place}</span> : null}
      {avatar ? <img className="av" src={avatar} alt="" /> : <span className="av av-i">{initialsOf(name)}</span>}
      <div className="pp-w">
        <b>
          <bdi>{name}</bdi>
          {you ? " (you)" : ""}
        </b>
        <span>{words}</span>
      </div>
      {actions ?? null}
      {place || actions ? null : (
        <span className="pp-ic">
          <GIcon name={micOn ? "mic" : "micoff"} size={16} />
          <GIcon name={cameraOn ? "cam" : "camoff"} size={16} />
        </span>
      )}
    </div>
  );
}

/** Past this many rows the list draws only what is in view: a live can hold thousands. */
const LONG_LIST = 60;
const PERSON_ROW = 56;
const CAPTION_ROW = 40;
/** Rows drawn beyond the edges of the view, so a quick scroll never shows a gap. */
const SPARE_ROWS = 8;

type PersonItem = { key: string; caption: string } | { key: string; face: MeetingFace; words: string };

/** Which items of a long list are in view; `tops` holds where each item starts, then where the last one ends. */
function useItemsInView(scroller: RefObject<HTMLElement | null>, list: RefObject<HTMLElement | null>, tops: number[]): { from: number; to: number } {
  const count = tops.length - 1;
  const [view, setView] = useState({ from: 0, to: count });
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const read = () => {
      const top = Math.max(0, el.scrollTop - (list.current?.offsetTop ?? 0));
      const spare = SPARE_ROWS * PERSON_ROW;
      // Why a fallback height: a box not yet measured still shows a first screenful.
      const last = top + (el.clientHeight || 12 * PERSON_ROW) + spare;
      let from = 0;
      while (from < count && tops[from + 1] <= top - spare) from++;
      let to = from;
      while (to < count && tops[to] < last) to++;
      setView((was) => (was.from === from && was.to === to ? was : { from, to }));
    };
    read();
    el.addEventListener("scroll", read, { passive: true });
    const watch = new ResizeObserver(read);
    watch.observe(el);
    return () => {
      el.removeEventListener("scroll", read);
      watch.disconnect();
    };
  }, [scroller, list, tops, count]);
  return { from: Math.min(view.from, count), to: Math.min(view.to, count) };
}

/** Everyone here: who speaks, the host, hands up, everyone else; and Find someone. */
function PeoplePane({
  faces,
  waiting,
  you,
  here,
  speakingId,
  viewer,
  hostAvatar,
  hostName,
  find,
  onFind,
  asHost,
}: {
  asHost?: HostColumn;
  faces: MeetingFace[];
  /** Before the live: the room's presence, by name and photo. */
  waiting: PresenceFace[] | null;
  /** Which of the presence's faces is this page's own person. */
  you: string | null;
  here: number;
  speakingId: string | null;
  viewer: { first: string; avatar: string | null };
  hostAvatar: string | null;
  hostName: string;
  find: string;
  onFind: (q: string) => void;
}): ReactNode {
  const q = find.trim().toLowerCase();
  const scroller = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const items = useMemo(() => {
    const out: PersonItem[] = [];
    if (waiting) return out;
    const hands = handsInOrder(faces);
    const handIds = new Set(hands.map((f) => f.id));
    const group = (key: string, caption: string, people: MeetingFace[], words: (f: MeetingFace, i: number) => string) => {
      const first = out.length;
      people.forEach((f, i) => {
        if (q && !f.name.toLowerCase().includes(q)) return;
        if (out.length === first) out.push({ key, caption });
        out.push({ key: f.id, face: f, words: words(f, i) });
      });
    };
    group("speaking", "Speaking", faces.filter((f) => f.id === speakingId), (f) => (f.host ? "Host · speaking" : "Speaking"));
    group("host", "Host", faces.filter((f) => f.host && f.id !== speakingId), (f) => `Host · ${faceWords(f).toLowerCase()}`);
    group("hands", "Hands up", hands, (_, i) => `Hand up, number ${i + 1}`);
    group("rest", "Everyone else", faces.filter((f) => f.id !== speakingId && !f.host && !handIds.has(f.id)), (f) => faceWords(f));
    return out;
  }, [faces, waiting, speakingId, q]);
  const tops = useMemo(() => {
    const at = [0];
    for (const item of items) at.push(at[at.length - 1] + ("caption" in item ? CAPTION_ROW : PERSON_ROW));
    return at;
  }, [items]);
  const windowed = items.length > LONG_LIST;
  const view = useItemsInView(scroller, list, windowed ? tops : [0]);
  const { from, to } = windowed ? view : { from: 0, to: items.length };
  const nobody = q ? <p className="pp-more">Nobody called “{find.trim()}” is here.</p> : null;
  let rows: ReactNode;
  if (waiting) {
    const named = waiting.filter((f) => !q || f.name.toLowerCase().includes(q));
    rows = (
      <>
        <p className="pp-g">Everyone here</p>
        {named.map((f) => (
          <PersonRow key={f.key} name={f.name} you={f.key === you} avatar={f.photo} words="Waiting for the start" micOn={false} cameraOn={false} />
        ))}
        {here > waiting.length && !q ? <p className="pp-more">and {fmt(here - waiting.length)} more.</p> : null}
        {named.length === 0 ? nobody : null}
      </>
    );
  } else {
    rows = (
      <>
        <div ref={list} style={windowed ? { paddingTop: tops[from], paddingBottom: tops[items.length] - tops[to] } : undefined}>
          {items.slice(from, to).map((item) =>
            "caption" in item ? (
              <p key={item.key} className="pp-g pp-fix">
                {item.caption}
              </p>
            ) : (
              <PersonRow
                key={item.key}
                fixed
                name={item.face.name}
                you={item.face.mine}
                avatar={item.face.host ? photoOfHost(item.face.name, hostName, hostAvatar) : item.face.mine ? viewer.avatar : null}
                words={item.words}
                micOn={item.face.micOn}
                cameraOn={item.face.cameraOn}
                actions={asHost && !item.face.mine ? <PersonActs face={item.face} host={asHost} /> : undefined}
              />
            ),
          )}
        </div>
        {items.length === 0 ? nobody : null}
      </>
    );
  }
  const total = waiting ? here : faces.length;
  return (
    <div className="pp" ref={scroller}>
      <p className="pp-t">
        Everyone here{" "}
        <span className="muted" style={{ fontSize: "var(--t-small)" }}>
          {fmt(total)}
        </span>
      </p>
      <label className="pp-find">
        <GIcon name="search" size={18} />
        <input type="search" dir="auto" placeholder="Find someone" aria-label="Find someone" value={find} onChange={(e) => onFind(e.target.value)} />
      </label>
      {rows}
    </div>
  );
}

/** What the host does to one person: bring them to the front, mute them, or take them out. */
function PersonActs({ face, host }: { face: MeetingFace; host: HostColumn }): ReactNode {
  const { meeting, tools } = host;
  return (
    <span className="hk-pact">
      {tools.spotlight ? (
        <button type="button" aria-label={`Spotlight ${face.name}`} title="Spotlight" onClick={() => void meeting.spotlight(face.id)}>
          <GIcon name="pin" size={16} />
        </button>
      ) : null}
      {tools.mute && !face.host && face.micOn ? (
        <button type="button" aria-label={`Mute ${face.name}`} title="Mute" onClick={() => void meeting.mute(face.id)}>
          <GIcon name="micoff" size={16} />
        </button>
      ) : null}
      {tools.remove && !face.host ? (
        <button type="button" aria-label={`Remove ${face.name}`} title="Remove from the live" onClick={() => void meeting.remove(face.id)}>
          <GIcon name="close" size={16} />
        </button>
      ) : null}
    </span>
  );
}

/** The hands, in the order they went up, and your own hand's button. */
function HandsPane({ faces, hostFirst, myPlace, onHand, host, chat }: { faces: MeetingFace[]; hostFirst: string; myPlace: number; onHand: () => void; host?: HostColumn; chat: RoomChatState }): ReactNode {
  const hands = handsInOrder(faces).filter((f) => !(host && f.mine));
  const { rule, asks, allowed } = chat.share;
  return (
    <div className="pp">
      <p className="pp-t">Hands</p>
      {host ? (
        <>
          <p className="pp-g">Who may share a screen</p>
          <div style={{ padding: "0 8px" }}>
            <ShareRules rule={rule} onRule={host.kit.onRule} />
          </div>
          {allowed ? <p className="pp-note"><bdi>{allowed.name}</bdi> may share their screen now.</p> : null}
          {asks.map((g) => (
            <div className="pp-r hk-row" key={g.registrantId}>
              <span className="av av-i">{initialsOf(g.name)}</span>
              <div className="pp-w">
                <b><bdi>{g.name}</bdi></b>
                <span>Asks to share their screen</span>
              </div>
              <span className="hk-rowact">
                <button type="button" className="hk-go sm" onClick={() => host.kit.onAnswer(g.registrantId, true)}>
                  Let them share
                </button>
                <button type="button" className="hk-no sm" onClick={() => host.kit.onAnswer(g.registrantId, false)}>
                  Not now
                </button>
              </span>
            </div>
          ))}
          <p className="pp-g hk-lblrow">
            <span>In the order they put a hand up</span>
            {hands.length > 1 ? (
              <button type="button" onClick={() => void host.meeting.lowerAllHands()}>
                Lower all
              </button>
            ) : null}
          </p>
        </>
      ) : (
        <p className="pp-note"><bdi>{hostFirst}</bdi> sees the hands in this order and calls on people when they can.</p>
      )}
      {hands.length ? (
        hands.map((f, i) => (
          <PersonRow
            key={f.id}
            name={f.name}
            you={f.mine}
            words={f.mine ? `Hand up, number ${i + 1}` : "Waiting"}
            micOn={f.micOn}
            cameraOn={f.cameraOn}
            place={i + 1}
            actions={
              host ? (
                <button type="button" className="hk-go sm" onClick={() => void host.meeting.callOn(f.id)}>
                  Call on
                </button>
              ) : undefined
            }
          />
        ))
      ) : (
        <p className="pp-more">No hands up right now.</p>
      )}
      {host ? (
        <p className="pp-note">Call on someone and their face comes to the front for everyone, with their microphone on. Their hand goes down by itself.</p>
      ) : (
      <div style={{ padding: "14px 8px" }}>
        <button className={myPlace ? "btn btn-g btn-m btn-w" : "btn btn-p btn-m btn-w"} type="button" onClick={onHand}>
          <GIcon name="hand" size={18} /> {myPlace ? "Lower my hand" : "Raise your hand"}
        </button>
      </div>
      )}
    </div>
  );
}

interface ColumnProps {
  tab: ColumnTab;
  onTab: (tab: ColumnTab) => void;
  /** Before the live there are no hands; before the room opens there is no room. */
  stage: "early" | "wait" | "live";
  wide: boolean;
  onTuck: () => void;
  session: WatchSession;
  hostFirst: string;
  viewer: { first: string; avatar: string | null };
  chat: RoomChatState;
  unreadRoom: number;
  unreadHost: boolean;
  opensAt: string;
  liveAt: number | null;
  ended: boolean;
  reconnecting: boolean;
  send: (msg: ChatToRoom) => boolean;
  faces: MeetingFace[];
  speakingId: string | null;
  myPlace: number;
  onHand: () => void;
  thread: WatchThread;
  find: string;
  onFind: (q: string) => void;
  host?: HostColumn;
}

export function LiveColumn(p: ColumnProps): ReactNode {
  const { session, hostFirst } = p;
  const profileFirst = firstWord(session.host_name);
  const handsUp = useMemo(() => handsInOrder(p.faces).length, [p.faces]);
  const host = p.host;
  const asked = useMemo(() => (host ? questionsIn(p.chat.lines).length : 0), [host, p.chat.lines]);
  const peopleCount = p.stage === "live" ? p.faces.length : (p.chat.here ?? 0);
  const tab = (which: ColumnTab, label: ReactNode, extra?: { off?: boolean; aria?: string }) => (
    <button
      className={["tab", which === "maya" ? "maya" : "", p.tab === which ? "on" : "", extra?.off ? "off" : ""].filter(Boolean).join(" ")}
      type="button"
      role="tab"
      aria-selected={p.tab === which}
      aria-disabled={extra?.off || undefined}
      aria-label={extra?.aria}
      onClick={() => p.onTab(which)}
    >
      {label}
    </button>
  );
  const hostFace = session.host_avatar_url ? <img className="av" src={session.host_avatar_url} alt="" /> : <span className="av av-i">{initialsOf(session.host_name)}</span>;
  return (
    <>
      <div className="col-h">
        <div className={host ? "tabs host" : "tabs"} role="tablist">
          {p.stage === "early"
            ? tab(
                "room",
                <>
                  Room <em>{p.opensAt}</em>
                </>,
                { off: true, aria: `Room, opens ${p.opensAt}` },
              )
            : tab(
                "room",
                <>
                  Room{p.unreadRoom > 0 && p.tab !== "room" ? <em>{p.unreadRoom > 99 ? "99+" : p.unreadRoom}</em> : null}
                </>,
              )}
          {p.stage === "early"
            ? null
            : tab(
                "people",
                <>
                  People <em>{fmt(peopleCount)}</em>
                </>,
              )}
          {p.stage === "live"
            ? tab(
                "hands",
                <>
                  Hands <em className={host?.lit && p.tab !== "hands" ? "lit" : undefined}>{fmt(host ? host.waiting : handsUp)}</em>
                </>,
              )
            : null}
          {host && (asked > 0 || p.tab === "questions") ? tab("questions", "Questions") : null}
          {host?.kit.moments ? tab("moments", "Moments") : null}
          {host ? null : tab(
            "maya",
            <>
              {hostFace}
              {p.stage === "early" ? ` ${profileFirst}` : null}
              {p.unreadHost && p.tab !== "maya" ? <i className="udot" /> : null}
            </>,
            { aria: p.stage === "early" ? undefined : `Your private chat with ${profileFirst}` },
          )}
        </div>
        <button className="ibtn col-x" type="button" aria-label={p.wide ? "Tuck the column away" : "Close"} onClick={p.onTuck}>
          <GIcon name={p.wide ? "tuck" : "close"} />
        </button>
      </div>
      <div className={p.tab === "room" ? "pane on" : "pane"}>
        {p.stage === "early" ? (
          <p className="pane-empty">
            <GIcon name="clock" size={22} />
            <b>The room opens at {p.opensAt}.</b>
            <span>Come back then to say hello. Until then, {profileFirst}’s chat is open.</span>
          </p>
        ) : (
          <GroupChat
            chat={p.chat}
            hostFirst={hostFirst}
            hostName={session.host_name}
            hostAvatar={session.host_avatar_url}
            opensAt={p.opensAt}
            liveAt={p.liveAt}
            ended={p.ended}
            reconnecting={p.reconnecting}
            send={p.send}
            onToHost={() => p.onTab("maya")}
            tools={host?.kit.lineTools}
          />
        )}
      </div>
      {p.stage === "early" ? null : (
        <div className={p.tab === "people" ? "pane on" : "pane"}>
          <PeoplePane
            faces={p.faces}
            waiting={p.stage === "wait" ? p.chat.faces : null}
            you={p.chat.you}
            here={p.chat.here ?? 0}
            speakingId={p.speakingId}
            viewer={p.viewer}
            hostAvatar={session.host_avatar_url}
            hostName={session.host_name}
            find={p.find}
            onFind={p.onFind}
            asHost={host}
          />
        </div>
      )}
      {p.stage === "live" ? (
        <div className={p.tab === "hands" ? "pane on" : "pane"}>
          <HandsPane faces={p.faces} hostFirst={hostFirst} myPlace={p.myPlace} onHand={p.onHand} host={host} chat={p.chat} />
        </div>
      ) : null}
      {host ? (
        <>
          {asked > 0 || p.tab === "questions" ? (
            <div className={p.tab === "questions" ? "pane on" : "pane"}>
              <HostQuestions chat={p.chat} pin={host.kit.lineTools.pin} />
            </div>
          ) : null}
          {host.kit.moments ? <div className={p.tab === "moments" ? "pane on" : "pane"}>{host.kit.moments}</div> : null}
        </>
      ) : (
        <div className={p.tab === "maya" ? "pane on" : "pane"}>
          <RoomChat session={session} thread={p.thread} host={firstWord(session.host_name)} sub={`Private. Only ${firstWord(session.host_name)} sees this.`} />
        </div>
      )}
    </>
  );
}
